import React from 'react';
import { DollarSign, TrendingUp, Percent, Calendar } from 'lucide-react';

const Financials = ({ formData, onInputChange }) => {
  const financialMetrics = [
    {
      id: 'burnRate',
      label: 'Monthly Burn Rate',
      icon: DollarSign,
      placeholder: 'e.g., $50K',
      description: 'Monthly cash burn rate'
    },
    {
      id: 'cac',
      label: 'Customer Acquisition Cost',
      icon: DollarSign,
      placeholder: 'e.g., $100',
      description: 'Cost to acquire a new customer'
    },
    {
      id: 'ltv',
      label: 'Lifetime Value',
      icon: DollarSign,
      placeholder: 'e.g., $500',
      description: 'Customer lifetime value'
    },
    {
      id: 'churn',
      label: 'Monthly Churn Rate',
      icon: Percent,
      placeholder: 'e.g., 2%',
      description: 'Monthly customer churn rate'
    },
    {
      id: 'breakeven',
      label: 'Breakeven Point',
      icon: Calendar,
      placeholder: 'e.g., Q3 2024',
      description: 'Projected breakeven date'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Financial Projections */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">5-Year Financial Projections</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {['year1', 'year2', 'year3', 'year4', 'year5'].map((year) => (
            <div key={year} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year {year.slice(-1)}
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.financials[year]}
                  onChange={(e) => onInputChange('financials', year, e.target.value)}
                  className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                  placeholder="e.g., $1M"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Financial Metrics */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Key Financial Metrics</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {financialMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div key={metric.id} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-white rounded-xl shadow-sm">
                    <Icon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {metric.label}
                    </label>
                    <p className="text-xs text-gray-500">{metric.description}</p>
                  </div>
                </div>
                <input
                  type="text"
                  value={formData.financials[metric.id]}
                  onChange={(e) => onInputChange('financials', metric.id, e.target.value)}
                  className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                  placeholder={metric.placeholder}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 p-4 bg-blue-50 rounded-xl">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Tips for Financials</h3>
        <ul className="text-sm text-blue-700 space-y-2">
          <li>• Be realistic with your projections and assumptions</li>
          <li>• Include key metrics that investors care about (CAC, LTV, Churn)</li>
          <li>• Show clear path to profitability</li>
          <li>• Use consistent formatting for currency values</li>
          <li>• Include both revenue and cost projections</li>
        </ul>
      </div>
    </div>
  );
};

export default Financials; 